import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PermissionGate from '../../../admin/components/PermissionGate';
import {
  buildConfigItemPayload,
  buildItemEditorState,
  formatJsonValue,
  formatValuePreview,
  getValueTypeGuidance,
  validateConfigItemEditor,
} from './configUtils';

function MetadataCard({ label, value, code }) {
  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={0.75}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={code ? { fontFamily: 'monospace', fontSize: '0.9rem', wordBreak: 'break-word' } : undefined}
        >
          {value}
        </Typography>
      </Stack>
    </Card>
  );
}

MetadataCard.propTypes = {
  code: PropTypes.bool,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

MetadataCard.defaultProps = {
  code: false,
};

function ValueEditor({ editorState, setEditorState, error }) {
  const commonProps = {
    fullWidth: true,
    error: Boolean(error),
    helperText: error || getValueTypeGuidance(editorState.valueType),
  };

  switch (editorState.valueType) {
    case 'bool':
      return (
        <Card variant="outlined" sx={{ p: 2.5 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Boolean Value</Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={editorState.boolValue}
                  onChange={(event) => setEditorState((prev) => ({ ...prev, boolValue: event.target.checked }))}
                />
              )}
              label={editorState.boolValue ? 'True' : 'False'}
            />
            <Typography variant="caption" color="text.secondary">
              {getValueTypeGuidance(editorState.valueType)}
            </Typography>
          </Stack>
        </Card>
      );
    case 'int':
      return (
        <TextField
          {...commonProps}
          type="number"
          label="Integer Value"
          value={editorState.intValue}
          onChange={(event) => setEditorState((prev) => ({ ...prev, intValue: event.target.value }))}
        />
      );
    case 'float':
      return (
        <TextField
          {...commonProps}
          type="number"
          label="Decimal Value"
          value={editorState.floatValue}
          onChange={(event) => setEditorState((prev) => ({ ...prev, floatValue: event.target.value }))}
        />
      );
    case 'json':
      return (
        <TextField
          {...commonProps}
          multiline
          minRows={12}
          label="JSON Value"
          value={editorState.jsonValue}
          onChange={(event) => setEditorState((prev) => ({ ...prev, jsonValue: event.target.value }))}
          InputProps={{
            sx: {
              fontFamily: 'monospace',
              alignItems: 'flex-start',
            },
          }}
        />
      );
    case 'null':
      return (
        <Alert severity="info" variant="outlined">
          This item is contract-typed as `null`. Saving will send a real JSON `null` value back to the backend.
        </Alert>
      );
    case 'string':
    default:
      return (
        <TextField
          {...commonProps}
          multiline={String(editorState.stringValue || '').length > 80}
          minRows={String(editorState.stringValue || '').length > 80 ? 4 : undefined}
          label="String Value"
          value={editorState.stringValue}
          onChange={(event) => setEditorState((prev) => ({ ...prev, stringValue: event.target.value }))}
        />
      );
  }
}

ValueEditor.propTypes = {
  editorState: PropTypes.object.isRequired,
  error: PropTypes.string,
  setEditorState: PropTypes.func.isRequired,
};

ValueEditor.defaultProps = {
  error: '',
};

export default function ConfigItemEditDialog({ item, open, submitting, onClose, onSubmit }) {
  const [editorState, setEditorState] = useState(buildItemEditorState(item));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setEditorState(buildItemEditorState(item));
    setErrors({});
  }, [item, open]);

  const payloadPreview = useMemo(() => {
    try {
      return formatValuePreview(buildConfigItemPayload(editorState), editorState.valueType);
    } catch (error) {
      return 'Preview unavailable until the value is valid.';
    }
  }, [editorState]);

  const handleSubmit = () => {
    const nextErrors = validateConfigItemEditor(editorState);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      return;
    }

    onSubmit({
      value: buildConfigItemPayload(editorState),
      reason: editorState.reason.trim(),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Config Item</DialogTitle>
      <DialogContent dividers>
        {item ? (
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={item.value_type} color="primary" variant="outlined" />
                <Chip label={item.is_active ? 'Active' : 'Inactive'} color={item.is_active ? 'success' : 'default'} variant="outlined" />
                <Chip label={item.is_secret ? 'Secret value' : 'Visible value'} color={item.is_secret ? 'warning' : 'default'} variant="outlined" />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {item.description || 'No description returned for this config item.'}
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <MetadataCard label="Group Key" value={item.group_key} code />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetadataCard label="Item Key" value={item.item_key} code />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetadataCard label="Value Type" value={item.value_type} code />
              </Grid>
              <Grid item xs={12}>
                <MetadataCard
                  label="Fallback Value"
                  value={item.is_secret ? 'Hidden secret fallback' : formatJsonValue(item.fallback_value ?? 'null')}
                  code
                />
              </Grid>
            </Grid>

            <Stack spacing={1}>
              <Typography variant="subtitle2">Value Editor</Typography>
              <Typography variant="caption" color="text.secondary">
                {getValueTypeGuidance(editorState.valueType)}
              </Typography>
            </Stack>

            <ValueEditor editorState={editorState} setEditorState={setEditorState} error={errors.value} />

            <Card variant="outlined" sx={{ p: 2.5 }}>
              <Stack spacing={1.25}>
                <Typography variant="subtitle2">Payload Preview</Typography>
                <Typography
                  component="pre"
                  variant="body2"
                  sx={{
                    m: 0,
                    p: 2,
                    borderRadius: 1.5,
                    bgcolor: 'grey.900',
                    color: 'grey.100',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {payloadPreview}
                </Typography>
              </Stack>
            </Card>

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Reason"
              value={editorState.reason}
              onChange={(event) => setEditorState((prev) => ({ ...prev, reason: event.target.value }))}
              helperText="Optional operator note saved with the change log when provided."
            />
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <PermissionGate moduleName="config" permissionCode="config.manage">
          <LoadingButton loading={submitting} variant="contained" onClick={handleSubmit}>
            Save Item
          </LoadingButton>
        </PermissionGate>
      </DialogActions>
    </Dialog>
  );
}

ConfigItemEditDialog.propTypes = {
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
};

ConfigItemEditDialog.defaultProps = {
  item: null,
};
